import React, {
  DependencyList,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

export type ClientConstructor<T> = new (
  baseUrl?: string,
  http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }
) => T;

export interface ApiCallGlobalConfig {
  onApiError?: (exception: unknown, apiCallOptions: UseApiCallOptions) => void;

  /**
   * Provide this funciton if you want to set your own baseUrl,
   * or to override the fetch() function to insert a authorization header.
   *
   * @example
   * ...
   *   constructClient: (clientConstructor) => {
   *    const isApi1 = isApi1Client(clientConstructor);
   *
   *    return new clientConstructor(isApi1 ? 'http://url1.com' : 'http://url2.com', {
   *      fetch: isApi1 ? fetch : authorizedFetch,
   *    });
   *   }
   * ...
   *
   * const authorizedFetch: typeof fetch = useCallback(
   *   (...args: Parameters<typeof fetch>) =>
   *     !userToken
   *       ? fetch(...args)
   *       : fetch(args[0], {
   *           ...(args[1] ?? {}),
   *           headers: {
   *             ...(args[1]?.headers ?? {}),
   *             Authorization: `Bearer ${userToken}`,
   *           },
   *         }),
   *   [userToken]
   * );
   */
  constructClient?: <C>(
    clientConstructor: ClientConstructor<C>,
    apiCallOptions: UseApiCallOptions
  ) => C;
}

export const ApiCallGlobalConfigContext =
  React.createContext<ApiCallGlobalConfig>({});

export enum RequestStatus {
  Idle,
  Fetching,
  Fetched,
  Error,
}

export interface UseApiCallOptions {
  initialRequestStatus?: RequestStatus;
  clearResponseOnError?: boolean;
  popModalBeforeError?: boolean;
  errorModalId?: string;
}

export interface ApiCall<Response, ExtraParams extends any[]> {
  /** Runs the api call and manages any server or connection errors that may occur in a user-friendly way.*/
  run(...extra: ExtraParams): Promise<[Response, null] | [null, unknown]>;

  /** Use this instead of run() if you want to apply different UseApiCallOptions when running*/
  advancedRun(
    extra: ExtraParams,
    options?: UseApiCallOptions
  ): Promise<[Response, null] | [null, unknown]>;

  response?: Response;
  error?: unknown;
  status: RequestStatus;
  setResponse: React.Dispatch<React.SetStateAction<Response | undefined>>;
  setStatus: React.Dispatch<React.SetStateAction<RequestStatus>>;
}

interface State<Response> {
  response?: Response;
  error?: unknown;
  status: RequestStatus;
}

export const useApiCall = <Client, Response, ExtraParams extends any[]>(
  clientConstructor: ClientConstructor<Client>,
  methodCaller: (client: Client, ...extra: ExtraParams) => Promise<Response>,
  methodCallerDeps?: DependencyList,
  options?: UseApiCallOptions
): ApiCall<Response, ExtraParams> => {
  const [state, setState] = useState<State<Response>>({
    response: undefined,
    error: undefined,
    status: options?.initialRequestStatus ?? RequestStatus.Idle,
  });
  const globalConfig = useContext(ApiCallGlobalConfigContext);
  const requestCalls = useRef(0);

  const getClient = useCallback(() => {
    return globalConfig.constructClient
      ? globalConfig.constructClient(clientConstructor, options ?? {})
      : new clientConstructor();
  }, [clientConstructor, globalConfig.constructClient, options]);

  const capturedMethodCaller = useCallback(methodCaller, [methodCallerDeps]);

  const advancedRun = useCallback(
    async (
      extra: ExtraParams,
      options?: UseApiCallOptions
    ): Promise<[Response, null] | [null, unknown]> => {
      setState((s) => ({ ...s, status: RequestStatus.Fetching }));
      requestCalls.current++;
      const myRequestNumber = requestCalls.current;

      try {
        const response = await capturedMethodCaller(getClient(), ...extra);

        // ignore setting the response if this call isn't the latest api call
        if (myRequestNumber === requestCalls.current) {
          setState((s) => ({
            ...s,
            status: RequestStatus.Fetched,
            response,
            error: undefined,
          }));
        }
        return [response, null];
      } catch (err) {
        if (options?.clearResponseOnError) {
          setState((s) => ({
            ...s,
            status: RequestStatus.Error,
            error: err,
            response: undefined,
          }));
        } else {
          setState((s) => ({ ...s, status: RequestStatus.Error, error: err }));
        }

        globalConfig.onApiError?.(err, options ?? {});

        return [null, err];
      }
    },
    [capturedMethodCaller, getClient, globalConfig.onApiError]
  );

  const run = useCallback(
    async (
      ...extra: ExtraParams
    ): Promise<[Response, null] | [null, unknown]> => {
      return await advancedRun(extra, options);
    },
    [advancedRun, options]
  );

  const setResponse: React.Dispatch<
    React.SetStateAction<Response | undefined>
  > = useCallback((setAction) => {
    requestCalls.current++;

    if (setAction instanceof Function) {
      setState((s) => ({
        ...s,
        response: setAction(s.response),
        status: RequestStatus.Fetched,
      }));
    } else {
      setState((s) => ({
        ...s,
        response: setAction,
        status: RequestStatus.Fetched,
      }));
    }
  }, []);

  const setStatus: React.Dispatch<React.SetStateAction<RequestStatus>> =
    useCallback((setAction) => {
      if (setAction instanceof Function) {
        setState((s) => ({ ...s, status: setAction(s.status) }));
      } else {
        setState((s) => ({ ...s, status: setAction }));
      }
    }, []);

  return {
    run,
    advancedRun,
    response: state.response,
    error: state.error,
    status: state.status,
    setResponse,
    setStatus,
  };
};
